/**
 * @fileoverview Hook for global time filter state management.
 * @module hooks/useTimeFilter
 *
 * This hook is called ONCE in MainApp.js and the result is passed to
 * TimeFilterProvider. Child components read the state via useTimeFilterContext().
 *
 * CANONICAL TIME AXIS: { startDate, endDate } — real dates, not lossy years.
 *
 * Backend vocabulary:
 *   - Matview endpoints: `years` (integer or null) — fiscal year filtering
 *   - Date-aware endpoints: `startDate`, `endDate` (YYYY-MM-DD strings)
 *
 * Translation layers:
 *   - timePeriod ('3y', '1m') → legacyPeriod ('last-3-years', 'last-30-days')
 *   - legacyPeriod → dateRange { startDate, endDate } via getDateRangeStrings()
 *   - legacyPeriod → years (DERIVED, lossy for sub-year periods)
 *
 * Sub-year filtering (1m/3m/6m):
 *   - dateRange correctly represents the window (e.g., 1m = last 30 days)
 *   - years collapses all sub-year periods to 1 (lossy — for matview compat)
 *   - Date-capable endpoints should use dateRange; matview endpoints use years
 */

import { useState, useCallback, useMemo } from 'react';
import { getDefaultTimePeriod, STANDARD_TIME_PERIODS } from '@techstack/ui';
import { getDateRangeStrings } from '../components/UnifiedTimeFilter/constants';

/**
 * Convert UI timePeriod to backend years parameter.
 * Matches the GAS filterContext.js pattern.
 *
 * @param {string} timePeriod - Period string from UI ('3y', '1m', 'all')
 * @returns {number|null} - Years for backend, or null for 'all time'
 */
export function timePeriodToYears(timePeriod) {
  if (!timePeriod || timePeriod === 'all') return null;

  // Parse: '1y'->1, '3y'->3, '5y'->5, '1m'->null (too short), '3m'->null, '6m'->1
  const match = timePeriod.match(/^(\d+)(y|m)$/);
  if (!match) return null;

  const num = parseInt(match[1], 10);
  const unit = match[2];

  if (unit === 'y') return num;
  if (unit === 'm') return num >= 12 ? Math.floor(num / 12) : 1; // Convert months to years, min 1

  return null;
}

/**
 * Convert shared UI timePeriod format to legacy UnifiedTimeFilter format.
 * Used for backward compatibility with components not yet migrated to central injection.
 *
 * Mapping:
 *   '1m'  -> 'last-30-days'
 *   '3m'  -> 'last-90-days'
 *   '6m'  -> 'last-6-months'
 *   '1y'  -> 'last-year'
 *   '3y'  -> 'last-3-years'
 *   '5y'  -> 'last-5-years'
 *   'all' -> 'all-time'
 *
 * @param {string} timePeriod - Period string from shared UI ('3y', '1m', 'all')
 * @returns {string} - Legacy format for UnifiedTimeFilter
 */
export function timePeriodToLegacy(timePeriod) {
  const mapping = {
    '1m': 'last-30-days',
    '3m': 'last-90-days',
    '6m': 'last-6-months',
    '1y': 'last-year',
    '3y': 'last-3-years',
    '5y': 'last-5-years',
    'all': 'all-time',
  };
  return mapping[timePeriod] || 'last-5-years';
}

/**
 * Derive years from a custom date range (for fiscal-year-bound endpoints).
 * Returns ceiling of span in years, minimum 1.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @returns {number}
 */
function deriveYearsFromDateRange(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 1;
  return Math.ceil(diffDays / 365) || 1;
}

/**
 * Hook providing global time filter state.
 * Called once in MainApp, result passed to TimeFilterProvider.
 *
 * Accepts either:
 * - Preset string: '3y', '1m', 'all' (derived dateRange)
 * - TimeRangeValue object: { startDate, endDate, years, preset } (direct dateRange)
 *
 * @returns {object} Time filter state and setters
 */
export function useTimeFilter() {
  // Default to 3y (same as GAS default)
  const [timePeriod, setTimePeriodRaw] = useState(() => getDefaultTimePeriod());

  // Custom date range state (set when user picks custom or FY quarter)
  // null = use derived dateRange from preset; object = use this directly
  const [customDateRange, setCustomDateRange] = useState(null);

  // Unified setter: accepts string preset OR TimeRangeValue object
  const setTimePeriod = useCallback((value) => {
    if (typeof value === 'string') {
      // Preset string - clear custom range, use derived
      setTimePeriodRaw(value);
      setCustomDateRange(null);
    } else if (value && typeof value === 'object') {
      // TimeRangeValue object - store preset and custom dates
      setTimePeriodRaw(value.preset || 'custom');
      if (value.preset === 'custom' || !value.preset || value.startDate) {
        // Custom range or FY quarter - store the actual dates
        setCustomDateRange({
          startDate: value.startDate,
          endDate: value.endDate,
        });
      } else {
        // Standard preset with dates - use derived (clear custom)
        setCustomDateRange(null);
      }
    }
  }, []);

  // Translate to backend vocabulary
  // For custom ranges, derive years from the date span
  const years = useMemo(() => {
    if (customDateRange?.startDate && customDateRange?.endDate) {
      return deriveYearsFromDateRange(customDateRange.startDate, customDateRange.endDate);
    }
    return timePeriodToYears(timePeriod);
  }, [timePeriod, customDateRange]);

  // Legacy format for backward compatibility with UnifiedTimeFilter-based components
  const legacyPeriod = useMemo(() => timePeriodToLegacy(timePeriod), [timePeriod]);

  // CANONICAL: Real date range for date-capable endpoints.
  // Custom ranges use stored values; presets use derived values.
  const dateRange = useMemo(() => {
    // Custom range takes priority
    if (customDateRange?.startDate && customDateRange?.endDate) {
      return customDateRange;
    }
    // Fallback to derived from preset
    if (!legacyPeriod || legacyPeriod === 'all-time') {
      return { startDate: null, endDate: null };
    }
    return getDateRangeStrings(legacyPeriod);
  }, [customDateRange, legacyPeriod]);

  return {
    timePeriod,
    setTimePeriod,
    years, // DERIVED: for fiscal-year-bound endpoints (matviews)
    legacyPeriod, // 'last-5-years' format for backward compatibility
    dateRange, // CANONICAL: { startDate, endDate } for date-capable endpoints
    // Expose periods for UI rendering if needed
    periods: STANDARD_TIME_PERIODS,
  };
}

export default useTimeFilter;
